import { GridRowId } from '@mui/x-data-grid/models/gridRows';
import { LoadClient } from '../../redux/api/load/load.model';
import { Company } from '../../redux/api/company/company.model';
import { QboAttributes } from '../../redux/api/orders/orders.model';
/**
 * Invoice load related information
 */
export interface InvoiceOrder {
  id: string;
  readable_id: string;
  invoice_load_sync_state: InvoiceState;
  customer_order_number?: string;
  factored?: INVOICE_FACTORING_STATUS_TYPE;
  tooltip?: string;
}

/**
 * Possible invoice sources
 */
export enum InvoiceSources {
  TB = 'Truckbase',
  QBO = 'Quickbooks'
}

export interface InvoiceResendData {
  sendTo: string[];
  tenantCC?: boolean;
}

export interface FactoringStatus {
  factoring_status: INVOICE_FACTORING_STATUS_TYPE;
  factoring_company: string;
  status_updated_at: string;
}

/**
 * Invoice summary response for v2 get invoices api
 */
export interface InvoiceSummaryResponse {
  id: string;
  invoice_number: string;
  invoice_id: string | null;
  status: INVOICE_STATUS;
  created_at: string;
  invoice_creation_date: string;
  due_date: string;
  amount: number;
  source: 'Truckbase' | 'Quickbooks';
  orders: InvoiceOrder[];
  client: LoadClient;
  invoice_order_sync_state: InvoiceState;
  bill_from_company: Company;
  division_id: string;
  updated_at: string;
  qb_desktop_attributes?: {
    error?: string | null;
    sync_attempted_at?: string | null;
  };
  qb_desktop_sync_status?: string;
  factoring_status: FactoringStatus;
}

export interface InvoiceItem {
  description: string;
  amount: number;
}

export type InvoiceDocument = {
  id: string;
  file_type: string;
  name: string;
  key: string;
  size: number;
};

/**
 * Invoice dto response for v2 get invoice api
 */
export interface InvoiceResponseV2 {
  id: string;
  invoice_number: string;
  invoice_id: string | null;
  status: string;
  sent?: boolean;
  created_at: string;
  due_date?: string;
  amount?: number;
  source: string;
  loads: InvoiceOrder[];
  client: LoadClient;
  invoice_items: InvoiceItem[];
  invoice_attachments: InvoiceDocument[];
  invoice_photos: InvoiceDocument[];
  special_instructions?: string;
}

export enum InvoiceState {
  INVOICE_OUTDATED = 'INVOICE_OUTDATED',
  UPDATE_NOT_SENT = 'UPDATE_NOT_SENT'
}

export interface InvoiceQboAttributes {
  division_qbo_credentials_id?: string | null;
  qbo_class_ref: QBOClassRef;
}

interface QBOClassRef {
  value: string;
  name?: string;
}

/**
 * Invoice dto response for v3 get invoice api
 */
export interface InvoiceResponseV3 {
  id: string;
  invoice_number: string;
  invoice_id: string | null;
  status: string;
  sent?: boolean;
  created_at: string;
  due_date?: string;
  amount?: number;
  source: string;
  orders: InvoiceOrder[];
  client: LoadClient;
  invoice_items: InvoiceItemV3[];
  invoice_attachments: InvoiceDocument[];
  invoice_photos: InvoiceDocument[];
  special_instructions?: string;
  is_legacy?: boolean;
  invoice_load_sync_state: InvoiceState;
  bill_from_company_id?: string | null;
  qbo_attributes?: InvoiceQboAttributes;
  division_id: string;
  sales_tax_id?: string | null;
  sales_tax_rate?: number | null;
}

export interface InvoiceIdsByDivision {
  division_id: string;
  ids: { orderId: string; invoiceId: string }[];
}

export interface InvoiceItem {
  description: string;
  amount: number;
}

export interface InvoiceItemV3 {
  description: string;
  amount: number;
  invoice_service_id?: string;
  invoice_service_name?: string;
  qbo_service_id?: string;
  qbo_service_type?: string;
  unit?: string;
  rate?: number;
  quantity?: number;
  order_id: string;
  sort_order: number;
  qbo_attributes?: QboAttributes | null;
}

/**
 * Data for creating Invoice
 */
export interface InvoiceCreateData {
  clientId?: string;
  orderIds: string[];
  source: InvoiceSources;
  sendInvoice: boolean;
  sendTo: string[];
  ccList?: string[];
  emailSubject?: string;
  emailBody?: string;
  invoiceItems: InvoiceItemV3[];
  invoiceAttachments: string[];
  invoicePhotos: string[];
  dueDate?: string;
  specialInstructions?: string;
  tenantCC?: boolean;
  withFactoringAssignment?: boolean;
  mergeAttachments?: boolean;
  zipAttachments?: boolean;
  billFromCompanyId: string;
  divisionId: string;
  tripOrderAttachmentIds: string[]; // TODO get rid of invoiceAttachments and invoicePhotos
  qboClassId?: string;
  salesTaxId?: string;
}

export interface InvoicePreviewPdfData {
  invoice: InvoiceCreateData | InvoiceUpdateDataV3;
  salesTaxEnabled: boolean;
  salesTaxRate: number;
  subtotal: number;
  salesTaxAmount: number;
  amount: number;
  salesTaxName: string;
}

/**
 * Data for updating Invoice
 */
export interface InvoiceUpdateData {
  clientId?: string;
  orderIds?: string[];
  sendInvoice: boolean;
  sendTo: string[];
  ccList?: string[];
  emailSubject?: string;
  emailBody?: string;
  invoiceItems: InvoiceItem[];
  invoiceAttachments: string[];
  invoicePhotos: string[];
  dueDate?: string;
  specialInstructions?: string;
  tenantCC?: boolean;
  billFromCompanyId?: string | null;
  qboClassRef: QBOClassRef;
}

/**
 * Data for updating Invoice V3
 */
export interface InvoiceUpdateDataV3 {
  source?: InvoiceSources;
  clientId?: string;
  orderIds?: string[];
  sendInvoice?: boolean;
  sendTo?: string[];
  ccList?: string[];
  emailSubject?: string;
  emailBody?: string;
  invoiceItems: InvoiceItem[];
  invoiceAttachments: string[];
  invoicePhotos: string[];
  dueDate?: string;
  specialInstructions?: string;
  tenantCC?: boolean;
  status?: INVOICE_STATUS;
  mergeAttachments?: boolean;
  zipAttachments?: boolean;
  billFromCompanyId?: string | null;
  sendAsCreated?: boolean;
  divisionId: string;
  skipAttachmentUpdates?: boolean;
  qboClassRef?: QBOClassRef;
  invoiceNumber?: string;
  invoiceId?: string | null;
  salesTaxId?: string;
  salesTaxRate?: number;
}

export interface ISearchInvoiceParams {
  from?: string;
  to?: string;
  invoiceIds?: GridRowId[];
  invoiceNumbers?: string[];
  statuses?: INVOICE_STATUS[];
  customerIds?: string[];
  orderNumbers?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  lastEditedDateFrom?: string;
  lastEditedDateTo?: string;
  factoringStatus?: FACTORING_FILTER_STATUS[];
  divisionId?: string[] | string;
  customerOrderNumbers?: string[];
  qbDesktopSyncStatuses?: string[];
}

export interface QBOCustomField {
  Name: string;
  StringValue: string;
  Type: string;
}

export enum INVOICE_STATUS {
  created = 'CREATED',
  sent = 'SENT',
  paid = 'PAID',
  overdue = 'OVERDUE',
  cancelled = 'CANCELLED'
}

export enum InvoiceStatusColor {
  CREATED = '#A855F7',
  SENT = '#A855F7',
  PAID = '#10B981',
  OVERDUE = '#EF4444',
  CANCELLED = '#EF4444'
}

export enum INVOICE_FACTORING_STATUS {
  factored = 'Sent',
  testingStatus = 'Testing',
  'error' = 'Error',
  'Error uploading to SFTP' = 'Error',
  'Internal Error' = 'Error',
  'Error Processing Invoices' = 'Error',
  'Error getting invoices' = 'Error',
  processing = 'Processing'
}

export enum INVOICE_FACTORING_STATUS_OVERVIEW {
  factored = 'Sent to factoring',
  'error' = 'Factoring error',
  'Error uploading to SFTP' = 'Factoring error',
  'Internal Error' = 'Factoring error',
  'Error Processing Invoices' = 'Factoring error',
  'Error getting invoices' = 'Factoring error'
}

export type INVOICE_FACTORING_STATUS_TYPE = keyof typeof INVOICE_FACTORING_STATUS;
export type INVOICE_FACTORING_STATUS_TYPE_OVERVIEW = keyof typeof INVOICE_FACTORING_STATUS_OVERVIEW;

export enum INVOICE_FACTORING_STATUS_COLOR {
  factored = '#10B981',
  testingStatus = '',
  'error' = '#EF4444',
  'Error uploading to SFTP' = '#EF4444',
  'Internal Error' = '#EF4444',
  'Error Processing Invoices' = '#EF4444',
  'Error getting invoices' = '#EF4444',
  processing = ''
}

export enum INVOICE_FACTORING_STATUS_TEXT_COLOR {
  factored = '#FFF',
  testingStatus = '#7e858e',
  'error' = '#FFF',
  'Error uploading to SFTP' = '#FFF',
  'Internal Error' = '#FFF',
  'Error Processing Invoices' = '#FFF',
  'Error getting invoices' = '#FFF',
  processing = '#7e858e'
}

export interface ValidateDivisionsResponse {
  valid: boolean;
  message: string;
}

export enum FACTORING_FILTER_STATUS {
  notSent = 'NOT_SENT',
  sentToFactoring = 'SENT_TO_FACTORING',
  error = 'ERROR'
}

export enum QB_DESKTOP_SYNC_STATUS {
  ERROR = 'ERROR',
  SYNCED = 'SYNCED',
  NOT_SYNCED = 'NOT_SYNCED'
}

export interface GetInvoiceNumberIdentifiersResponse {
  invoice_id: string;
}

export interface GetInvoiceNumberIdentifiersFilterParams {
  invoice_id?: string;
}
